import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MemberOnboardingModule from "components/member/MemberOnboardingModule";
import PersonalizedTasks from "components/tasks/PersonalizedTasks";
import WelcomeHeader from "components/member/home/WelcomeHeader";
import TaskSummary from "components/member/home/TaskSummary";
import { ActivityFeed } from "components/member/home/ActivityFeed";
import TaskList from "components/member/home/TaskList";
import AdvisorInfo from "components/member/home/AdvisorInfo";
import CalendarTasks from "components/member/home/CalendarTasks";
import AddTask from "components/member/home/AddTask";
import FlexBox from "components/common/FlexBox";
import UpcomingVideoCalls from "components/common/UpcomingVideoCalls";
import NoticeBanner from "components/common/NoticeBanner";
import FeatureSuggestionsModule from "components/member/FeatureSuggestionsModule";
import GatedComponent, {
  FEATURE_FLAGS,
} from "components/helpers/GatedComponent";
import { HomeLayoutProps } from "./types";

export default ({
  data,
  loading,
  currentUser,
  integrationCount,
  integrationsLoading,
  calendarTwoWaySyncUpsell,
  showAnimations,
  isDesktop,
  isSystemAdvisor,
  isDeactivated,
  productPrompts,
  handleDismissReferralBanner,
  handleReferralBannerLinkClick,
  handleDismissFeatureSuggestions,
  handleDismissLightningBanner,
  handleLightningBannerClick,
  handleDismissReviewPrompt,
  openReview,
}: HomeLayoutProps) => {
  return (
    <Box sx={{ alignSelf: "center", maxWidth: "1000px", width: "100%" }}>
      <Box
        sx={{
          display: isDesktop ? "grid" : "flex",
          flexDirection: !isDesktop ? "column" : "",
          gridTemplateColumns: {
            sm: "1fr",
            md: "40% 60%",
          },
          gap: "24px",
        }}
      >
        <Box sx={{ gridColumn: "1 / -1" }}>
          <WelcomeHeader user={currentUser} />
        </Box>

        <Box sx={{ gridColumn: "1 / -1" }}>
          <MemberOnboardingModule
            member={data?.member!}
            family={data?.family!}
            showGoogleCalendarSyncUpsell={calendarTwoWaySyncUpsell}
          />
        </Box>

        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <UpcomingVideoCalls family={data?.family!} />
          {!isDeactivated &&
            productPrompts?.referralPromoDismissed !== "dismissed" && (
              <NoticeBanner
                title="🎉 Earn $50 for Every Friend You Refer!"
                subtext={
                  <Typography variant="body">
                    Love Faye? Share the love! Give friends 20% off their first
                    month, and get a $50 credit when they get started with an
                    Advisor. There's no limit to how much you can earn!{" "}
                    <Link
                      component={RouterLink}
                      to="/invite"
                      fontWeight={700}
                      onClick={handleReferralBannerLinkClick}
                    >
                      Share your link here!
                    </Link>
                  </Typography>
                }
                dismissible={true}
                onDismiss={handleDismissReferralBanner}
              />
            )}
          <GatedComponent
            featureName={FEATURE_FLAGS.DELEGATE_PHONE_CALL.value}
            includeAdmin={false}
            returnNull={true}
          >
            {productPrompts?.lightningTaskBannerDismissed !== "dismissed" && (
              <NoticeBanner
                title="⚡ Need to fire off a quick task?"
                subtext="Create a lightning task for a quick call or other fast action. We will send it to our queue for the first available advisor to pick up."
                buttonText="Try now!"
                dismissible={true}
                onDismiss={handleDismissLightningBanner}
                onClick={handleLightningBannerClick}
              />
            )}
          </GatedComponent>
        </Box>

        {!isDeactivated && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "24px",
              }}
            >
              <FlexBox
                variant="contained"
                direction="column"
                justifyContent="space-between"
              >
                <AdvisorInfo
                  advisor={data?.primaryAdvisor!}
                  isDeactivated={isDeactivated}
                  openReview={openReview}
                  familyCreatedAtSec={data?.family?.createdAtSec}
                  productPrompts={productPrompts}
                  onDismissReview={handleDismissReviewPrompt}
                />
                {!isSystemAdvisor && (
                  <TaskSummary planUtilization={data?.planUtilization} />
                )}
              </FlexBox>
              <FlexBox variant="contained" direction="column" flex={0}>
                <AddTask family={data?.family!} />
              </FlexBox>
            </Box>

            <FlexBox
              variant="contained"
              direction="column"
              enableHeightAnimation={true}
              animationDuration="1.5s"
              maxHeight={showAnimations ? "1000px" : "420px"}
            >
              <ActivityFeed family={data?.family} />
            </FlexBox>
          </>
        )}

        {productPrompts?.featureSuggestionsDismissed !== "dismissed" && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <FeatureSuggestionsModule
              onDismiss={handleDismissFeatureSuggestions}
            />
          </Box>
        )}

        <FlexBox
          variant="contained"
          direction="column"
          enableHeightAnimation={true}
          animationDuration="1.5s"
          maxHeight={showAnimations ? "1000px" : "420px"}
        >
          <TaskList
            tasks={data?.activeTasks || []}
            loading={loading || !data}
          />
        </FlexBox>

        <FlexBox
          variant="contained"
          direction="column"
          enableHeightAnimation={true}
          animationDuration="1.5s"
          maxHeight={showAnimations ? "1000px" : "420px"}
        >
          <CalendarTasks
            family={data?.family!}
            integrationCount={integrationCount}
            forceLoading={!data || integrationsLoading}
          />
        </FlexBox>

        <FlexBox
          variant="contained"
          direction="column"
          enableHeightAnimation={true}
          animationDuration="1.5s"
          maxHeight={showAnimations ? "1000px" : "420px"}
          sx={{ gridColumn: "1 / -1" }}
        >
          <PersonalizedTasks
            family={data?.family!}
            integrationCount={integrationCount}
            forceLoading={integrationsLoading}
          />
        </FlexBox>
      </Box>
    </Box>
  );
};
